import { Box, Typography, useTheme, Divider } from "@mui/material";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  format_Number,
} from "../../utils/utils";
import { setUpsaleProductPrice } from "../../store/slice/Salon.slice";

const YourBookingCard = ({ Css }) => {
  const theme = useTheme();

  dayjs.extend(advancedFormat);
  const {
    selectedGenderType,
    selectedTreatmentType,
    selectedProffesionalDetails,
    selectedDate,
    selectedTime,
    selectedUpsaleProducts,
    quantities,
    totalPrice,
    languageWords,
  } = useSelector((store) => store?.salon);

  const [from, to] = (selectedTime?.timeRange).split(" - ");

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedUpsaleProducts) {
      const totalSum =
        selectedUpsaleProducts?.reduce(
          (acc, product) =>
            acc + quantities[product.idproduct] * product.price,
          0
        ) || 0;
      dispatch(setUpsaleProductPrice(totalSum));
    }
  }, [selectedUpsaleProducts]);

  const formatTranslatedDate = (selectedDate, languageWords) => {
    const formattedDate = dayjs(selectedDate, "DD-MM-YYYY").format(
      "dddd, DD MMMM YYYY"
    );

    const translatedDate = formattedDate
      .replace(
        /\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/g,
        (match) =>
          languageWords?.[match === "Monday" ? "monday" : match] || match
      )
      .replace(
        /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g,
        (match) => languageWords?.[match] || match
      );

    return translatedDate;
  };

  return (
    <Accordion
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        "&:before": { display: "none" },
      }}
    >
      {/* ===== Accordion Header ===== */}
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          {/* Title — truncates if too long */}
          <Typography
            fontWeight="bold"
            noWrap
            sx={{
              fontSize: "1rem",
              color: Css?.salonCardSectionHeadingClr,
              flex: "1 1 0",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedTreatmentType?.name}
          </Typography>

          {/* Time range */}
          <Typography
            sx={{
              color: Css?.saloncardSectionLabelClr,
              fontSize: "12px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flexShrink: 0,
            }}
          >
            {languageWords?.From}:{" "}
            <span
              style={{ fontWeight: "bold", color: Css?.salonCardSectionInfoClr }}
            >
              {from}
            </span>{" "}
            {languageWords?.To}:{" "}
            <span
              style={{ fontWeight: "bold", color: Css?.salonCardSectionInfoClr }}
            >
              {to}
            </span>
          </Typography>

          {/* Price — stays on same line, never pushed off */}
          {totalPrice > 0 && (
            <Typography
              
              sx={{
                fontSize: "14px",
                color: Css?.salonCardSectionPriceClr,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              €{format_Number(totalPrice.toFixed(2))}
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      {/* ===== Accordion Body ===== */}
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Gender & Stylist */}
          <Box>
            <Typography
              variant="body2"
              sx={{ color: Css?.salonCardSectionInfoClr }}
            >
              {languageWords?.Gender}:{" "}
              <strong>{selectedGenderType?.gender}</strong>
            </Typography>

            <Typography
              variant="body2"
              sx={{ color: Css?.salonCardSectionInfoClr }}
            >
              {languageWords?.Stylist || "Stylist"}:{" "}
              <strong>
                {typeof selectedProffesionalDetails === "string"
                  ? selectedProffesionalDetails
                  : `${selectedProffesionalDetails?.firstName} ${selectedProffesionalDetails?.lastName}`}
              </strong>
            </Typography>
          </Box>

          {/* Date & Time */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <AccessTimeIcon sx={{ color: Css?.salonCardSectionIconClr }} />
            <Box>
              <Typography variant="body2" color={Css?.salonCardSectionInfoClr}>
                {formatTranslatedDate(selectedDate, languageWords)}
              </Typography>
              <Typography
                fontWeight={600}
                sx={{
                  color: Css?.saloncardSectionLabelClr,
                  fontSize: "12px",
                }}
              >
                {languageWords?.From}:{" "}
                <span style={{ color: theme?.details?.detailsDateTextColor }}>
                  {from}
                </span>{" "}
                {languageWords?.To}:{" "}
                <span style={{ color: theme?.details?.detailsDateTextColor }}>
                  {to}
                </span>
              </Typography>
            </Box>
          </Box>
          <Divider />
          {selectedUpsaleProducts?.map((item) => (
                <Box
                  key={item.idproduct}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                    color: Css?.salonCardSectionInfoClr  ?? "#000",
                  
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <ProductionQuantityLimitsIcon
                      sx={{ color:Css?.salonCardSectionIconClr , fontSize: "20px" }}
                    />
                      <Typography
                  
                  >
                    {quantities[item.idproduct]}x {item.productName}
                  </Typography>
                  </Box>
                  {item?.price > 0 && (
                         <Typography
                         variant="body2"
                         
                         sx={{ color: Css?.salonCardSectionInfoClr,mr:3 }}
                       >
                         €
                         {format_Number(
                           (quantities[item.idproduct] * item.price).toFixed(2)
                         )}
                       </Typography>
                  )}
                </Box>
              ))}
      
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default YourBookingCard;
